import React, { useState, useEffect, useRef, useCallback } from "react";
import PopStyle from "../../style/PopUp.module.css";
import Style from "../../style/components/chat/ChatForword.module.css";
import { CiSearch } from "react-icons/ci";
import FallbackAvatar from "../ui/FallbackAvatar/FallbackAvatar";
import { useForwardLst } from "../../hooks/useForward";
import { renderQuillDelta } from "../../utils/textHelper";
import PageLoader from "../ui/Loader/PageLoader";

const ChatForward = ({
  onClose,
  messageId,
  chatUniqueId,
  pageFrom,
  handleForward,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const { user, isLoading } = useForwardLst(
    searchTerm,
    messageId,
    chatUniqueId,
    pageFrom
  );
  const apiUrl = process.env.REACT_APP_API_URL;
  const popupRef = useRef(null);

  const formatTime = useCallback((timeString) => {
    if (!timeString) return "Unknown time";
    return timeString;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Unknown date";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  }, []);

  const getFileType = useCallback((fileName) => {
    if (!fileName) return "generic";

    const extension = fileName.split(".").pop()?.toLowerCase() || "";
    const fileTypes = {
      pdf: "pdf",
      doc: "word",
      docx: "word",
      xls: "excel",
      xlsx: "excel",
      ppt: "powerpoint",
      pptx: "powerpoint",
      zip: "archive",
      rar: "archive",
      jpg: "image",
      jpeg: "image",
      png: "image",
      gif: "image",
      txt: "text",
      py: "code",
      js: "code",
      html: "code",
      css: "code",
    };
    return fileTypes[extension] || "generic";
  }, []);

  const getFileIcon = useCallback((fileType) => {
    const icons = {
      pdf: "📄",
      word: "📝",
      excel: "📊",
      powerpoint: "📑",
      archive: "📦",
      image: "🖼️",
      text: "📃",
      code: "💻",
      generic: "📎",
    };
    return icons[fileType] || "📎";
  }, []);

  const handleUserSelect = useCallback((userItem) => {
    setSelectedUser(userItem);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const hasMessageContent = useCallback(() => {
    if (!user.forward_message) return false;

    const hasText =
      user.forward_message.message &&
      user.forward_message.message !== "[]" &&
      user.forward_message.message.trim() !== "";
    const hasFiles =
      user.forward_message.file_url && user.forward_message.file_url.length > 0;

    return hasText || hasFiles;
  }, [user.forward_message]);

  const getUserName = useCallback((userItem) => {
    return userItem?.name || "Unknown User";
  }, []);

  const getUserType = useCallback((userItem) => {
    if (userItem?.is_group_chat === "1") return "Group";
    if (userItem?.is_group_chat === "channel") return "Channel";
    return "User";
  }, []);

  return (
    <div className={PopStyle.popupBackgroundNormal}>
      <div className={Style.ChatForwordMain} ref={popupRef}>
        <div className={Style.ChatForwordContainer}>
          {user.forward_message && hasMessageContent() && (
            <div className={Style.forwardPreview}>
              <div className={Style.forwardPreviewHeader}>
                <h3>Forward Message</h3>
                <div className={Style.forwardPreviewMeta}>
                  <span className={Style.forwardDate}>
                    {formatDate(user.forward_message.date)} at{" "}
                    {formatTime(user.forward_message.time)}
                  </span>
                </div>
              </div>

              <div className={Style.forwardMessageContent}>
                {user.forward_message.message &&
                  user.forward_message.message !== "[]" &&
                  user.forward_message.message.trim() !== "" && (
                    <div className={Style.forwardMessageText}>
                      {renderQuillDelta(user.forward_message.message)}
                    </div>
                  )}

                {user.forward_message.file_url &&
                  user.forward_message.file_url.length > 0 && (
                    <div className={Style.forwardFiles}>
                      <div className={Style.filesHeader}>
                        <span className={Style.filesCount}>
                          {user.forward_message.file_url.length} file(s)
                          attached
                        </span>
                      </div>
                      <div className={Style.filesList}>
                        {user.forward_message.file_url.map((file, index) => {
                          const fileType = getFileType(file.file_name);
                          const fileIcon = getFileIcon(fileType);

                          return (
                            <div
                              key={index}
                              className={`${Style.fileItem} ${Style[fileType]}`}
                            >
                              <div className={Style.fileIcon}>{fileIcon}</div>
                              <div className={Style.fileInfo}>
                                <div className={Style.fileName}>
                                  {file.file_name || "Unknown file"}
                                </div>
                                <div className={Style.fileSize}>
                                  {file.file_size_kb || "Unknown size"}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          <div className={Style.searchContainer}>
            <input
              type="text"
              placeholder="Search users or groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={Style.searchInput}
            />
            <CiSearch size={16} className={Style.searchIcon} />
          </div>

          <div className={Style.usersList}>
            {isLoading ? (
              <div className={Style.loadingState}>
                {" "}
                <PageLoader />
              </div>
            ) : user?.combined_user_list?.length > 0 ? (
              user.combined_user_list.map((userItem) => (
                <div
                  key={
                    userItem.chat_unique ||
                    userItem.private_chat_id ||
                    userItem.register_id ||
                    `user-${Math.random()}`
                  }
                  className={`${Style.userItem} ${
                    selectedUser?.chat_unique === userItem.chat_unique
                      ? Style.selected
                      : ""
                  }`}
                  onClick={() => handleUserSelect(userItem)}
                >
                  <div className={Style.radioContainer}>
                    <input
                      type="radio"
                      name="forwardUser"
                      checked={
                        selectedUser?.chat_unique === userItem.chat_unique
                      }
                      onChange={() => handleUserSelect(userItem)}
                      className={Style.radioInput}
                    />
                  </div>

                  <div className={Style.avatar}>
                    {userItem?.emp_pic ? (
                      <img
                        src={`${apiUrl}${userItem.emp_pic}`}
                        className={Style.avatarImg}
                        alt="Profile"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <FallbackAvatar name={getUserName(userItem)} size={30} />
                    )}

                    {userItem.online_status && (
                      <span className={Style.onlineIndicator}></span>
                    )}
                  </div>

                  <div className={Style.userInfo}>
                    <div className={Style.userName}>
                      {getUserName(userItem)}
                      {userItem.is_you === "You" && " (You)"}
                    </div>
                    <div className={Style.userType}>
                      {getUserType(userItem)}
                      {userItem.unread_count > 0 && (
                        <span className={Style.unreadBadge}>
                          {userItem.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={Style.noResults}>
                {searchTerm ? "No users or groups found" : "No users available"}
              </div>
            )}
          </div>

          <div className={Style.forwardActions}>
            <button
              className={Style.cancelButton}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className={`${Style.forwardButton} ${
                !selectedUser ? Style.disabled : ""
              }`}
              onClick={() => {
                handleForward(user.forward_message, selectedUser);
              }}
              disabled={!selectedUser}
              type="button"
            >
              Forward Message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatForward;
